<template>
  <div v-if="visible" data-testid="sensor_events">
    <v-divider class="my-4"/>
    <content-header
      titleClass="title mr-2"
      :description="sensorDetails"
      stackHeadingOnMobile
      :to="`sensors/${sensor.id}`"
      class="my-3"
    >
      <template #title>{{ sensor.name }}</template>
      <template #icon><sensor-icon :sensorType="sensor.type" /></template>
      <template #subtitle>
        <div class="sensor-events__subtitle">
          <div class="sensor-events__place subheading" data-testid="content-subtitle">{{ sensor.place }}</div>
          <div
            class="sensor-events__last-request caption hidden-md-and-up"
            data-testid="content-last-event-request-mobile"
          >{{ sensorDetails }}</div>
        </div>
      </template>
      <v-row no-gutters class="flex-nowrap">
        <v-spacer/>
        <rat-graph-button
          v-if="isRatMonitor"
          :sensor="sensor"
          :requestParams="exportParams"
          :disabled="sensorState === SENSOR_STATE.NO_DATA"
          class="ml-2 hidden-sm-and-down"
        />
        <sensor-events-export-button
          :sensor="sensor"
          :requestParams="exportParams"
          :disabled="sensorState === SENSOR_STATE.NO_DATA"
          class="ml-2 hidden-sm-and-down"
          data-testid="export_csv"
        />
      </v-row>
    </content-header>
    <div
      v-if="sensorState === SENSOR_STATE.NO_DETECTION"
      class="sensor-status sensor-status--no-activity d-flex align-center pa-4"
      data-testid="sensor-status-no-detection"
    >
      <span class="sensor-status__dot"></span>
      <span class="sensor-status__text">{{ isRatMonitor ? $t('noDetection') : $t('noDetectionGeneric') }}</span>
    </div>
    <div
      v-else-if="sensorState === SENSOR_STATE.NO_DATA"
      class="sensor-status sensor-status--warning d-flex align-center pa-4"
      data-testid="sensor-status-no-data"
    >
      <span class="sensor-status__dot"></span>
      <span class="sensor-status__text">{{ $t('noData') }}</span>
    </div>
    <template v-if="sensorState === SENSOR_STATE.DETECTED">
      <div class="mb-4">
        <round-button
          class="expand-btn"
          :icon="expanded ? 'fa-chevron-circle-up' : 'fa-chevron-circle-down'"
          @click="$emit('update:expanded', !expanded)"
          v-if="detectedCount > 0"
        >
          {{ $mpt($t('button.expand')) }}
        </round-button>
      </div>
      <v-row wrap>
        <template v-for="event in displayedEvents">
          <v-flex md3 xs12 class="pa-2 event-wrapper" :key="event.id">
            <event
              class="event"
              :event="event"
              :editable="isEditable"
              @event:selected="$emit('event:selected', event, events)"
              @event:hidden="v => updateEventHidden(event, v)"
              />
          </v-flex>
        </template>
      </v-row>
      <div class="mt-4 bottom-buttons">
        <div class="d-flex justify-content-between" ref="listEnd">
          <v-progress-linear
            color="primary"
            indeterminate
            rounded
            height="6"
            v-if="eventsLoading"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<i18n lang="yaml">
ja:
  button:
    expand: 'センサーを展開'
    export: 'CSV出力'
  noDetection: 'ネズミ検出なし'
  noDetectionGeneric: '検出なし'
  noData: 'データ未受信'

  label:
    installation_date: '設置日 : {date}'
    threshold: 'しきい値 : {value}'
    last_event_request_time: '最終受信日時 : {timestamp}'

en:
  button:
    expand: 'Expand'
    export: 'Export CSV'
  noDetection: 'No rat detected'
  noDetectionGeneric: 'No detection'
  noData: 'No data received'

  label:
    installation_date: 'Installation date: {date}'
    threshold: 'Threshold: {value}'
    last_event_request_time: 'Last Request Time: {timestamp}'
</i18n>

<script>
import DateTimeFormatting from '@/mixins/dateTimeFormatting';
import EventContainer from '@/mixins/eventContainer';
import Platform from '@/mixins/platform';
import SensorTypes, { SENSOR_TYPES } from '@/mixins/sensorTypes';

import RoundButton from '@/components/atoms/RoundButton';
import ContentHeader from '@/components/ContentHeader';
import SensorIcon from '@/components/SensorIcon';

import SensorEventsExportButton from './SensorEventsExportButton';
import RatGraphButton from './RatGraphButton';
import Event from './Event';

const SCROLL_THROTTLE_MS = 200;

export const SENSOR_STATE = {
  LOADING: 'loading',
  DETECTED: 'detected',
  NO_DETECTION: 'no-detection',
  NO_DATA: 'no-data',
};

export default {
  name: 'sensor-events',
  mixins: [
    DateTimeFormatting,
    EventContainer,
    Platform,
    SensorTypes,
  ],
  props: {
    sensor: Object,
    requestParams: {
      type: Object,
      required: true,
    },
    expanded: Boolean,
  },
  data() {
    return {
      displayedCount: 4,
      scrollListener: this._.throttle(this.showNewElementsIfNecessary, SCROLL_THROTTLE_MS),
    };
  },
  components: {
    ContentHeader,
    RoundButton,
    SensorIcon,
    SensorEventsExportButton,
    RatGraphButton,
    Event,
  },
  computed: {
    detectedCount() {
      return this.events.filter(ev => ev.detected).length;
    },
    displayedEvents() {
      return this._.take(this.events, this.displayedCount);
    },
    exportParams() {
      return {
        ...this.requestParams,
        with_undetected: 1,
      };
    },
    isEditable() {
      return this.sensor.permissions.includes('full');
    },
    onEventUpdate() {
      return this.$store.getters.getUpdatedEvent(this.sensorId);
    },
    sensorDetails() {
      let details = '';

      if (this.sensor.installed_on) {
        const installedOn = this.formatDate(this.sensor.installed_on);
        details += this.$t('label.installation_date', { date: installedOn });
      }

      if (this.sensor.threshold) {
        if (this.sensor.installed_on) {
          details += ' / ';
        }
        details += this.$t('label.threshold', { value: this.sensor.threshold });
      }

      if (details) {
        details += ' / ';
      }
      const timestamp = this.formatTimestamp(this.sensor.last_event_request_time);
      details += this.$t('label.last_event_request_time', { timestamp });

      return details;
    },
    sensorId() {
      return this.sensor.id;
    },
    shouldReload() {
      return this.$store.getters.shouldReloadSensor(this.sensorId);
    },
    isRatMonitor() {
      return this.sensor.type === SENSOR_TYPES.RAT;
    },
    sensorState() {
      if (this.hasUnfilteredEvents == null) {
        return SENSOR_STATE.LOADING;
      }
      if (this.events.length > 0) {
        return SENSOR_STATE.DETECTED;
      }
      if (this.hasUnfilteredEvents) {
        return SENSOR_STATE.NO_DETECTION;
      }
      return SENSOR_STATE.NO_DATA;
    },
    totalCount() {
      return this.events.length;
    },
    visible() {
      return this.sensorState !== SENSOR_STATE.LOADING;
    },
  },
  methods: {
    isListEndInViewport() {
      if (!this.$refs.listEnd) {
        return false;
      }
      const rect = this.$refs.listEnd.getBoundingClientRect();
      const html = document.documentElement;
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || html.clientHeight) &&
        rect.right <= (window.innerWidth || html.clientWidth)
      );
    },
    showNewElementsIfNecessary: async function () {
      if (!this.expanded || !this.isListEndInViewport()) {
        return;
      }
      while (this.displayedCount < this.totalCount && this.isListEndInViewport()) {
        this.displayedCount = Math.min(
          this.displayedCount + (this.isMobile ? 1 : 4),
          this.totalCount,
        );
        // eslint-disable-next-line no-await-in-loop
        await this.$nextTick();
      }
      if (this.isListEndInViewport() && !this.allEventsLoaded && !this.eventsLoading) {
        await this.loadNextEvents(40);
        while (this.displayedCount < this.totalCount && this.isListEndInViewport()) {
          this.displayedCount = Math.min(
            this.displayedCount + (this.isMobile ? 1 : 4),
            this.totalCount,
          );
          // eslint-disable-next-line no-await-in-loop
          await this.$nextTick();
        }
      }
    },
    updateEventHidden: async function (event, value) {
      event.hidden = value;
      await this.$store.dispatch('updateSensorEvent', {
        event,
        attributes: { hidden: value },
      });
    },
  },
  created() {
    this.SENSOR_STATE = SENSOR_STATE;
  },
  mounted() {
    window.addEventListener('scroll', this.scrollListener);
  },
  destroyed() {
    window.removeEventListener('scroll', this.scrollListener);
  },
  watch: {
    sensorState(state) {
      if (state !== SENSOR_STATE.LOADING) {
        this.$emit('stateChange', state);
      }
    },
    expanded(value) {
      if (value) {
        this.showNewElementsIfNecessary();
      } else {
        this.displayedCount = 4;
      }
    },
    onEventUpdate(updated) {
      if (updated) {
        const index = this.events.findIndex(ev => ev.id === updated.id);
        if (index !== -1) {
          this.events.splice(index, 1, updated);
        }
      }
    },
    shouldReload: async function (value) {
      if (value) {
        this.clearEvents();
        await this.loadNextEvents(this.displayedCount);
        this.$store.dispatch('onReloadSensorFinished', this.sensorId);
      }
    },
    visible(value) {
      if (value) {
        this.$emit('becameVisible');
      }
    },
  },
};
</script>

<style lang="sass" scoped>
@import 'vuetify/src/styles/styles.sass'

.sensor-events__subtitle
  min-width: 0

.sensor-events__last-request
  line-height: 1.4

@media #{map-get($display-breakpoints, 'md-and-up')}
  .sensor-events__place
    white-space: nowrap

@media #{map-get($display-breakpoints, 'sm-and-down')}
  .sensor-events__place
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  .sensor-events__subtitle
    display: flex
    flex-direction: column
    gap: 4px
    width: 100%

  .sensor-events__last-request
    width: 100%

.event-wrapper
  @media #{map-get($display-breakpoints, 'md-and-up')}
    // Keep a 4:3 aspect ratio
    height: calc((100vw / 4) * (3 / 4))

.event
  height: 100%

.sensor-status
  gap: 8px

  &__dot
    width: 12px
    height: 12px
    border-radius: 50%
    flex-shrink: 0

  &--no-activity &__dot
    background: #388e3c

  &--warning &__dot
    background: #9e9e9e
</style>
